/**
 * Payer Enrollments Step
 *
 * Fourth and final step in billing setup wizard - initiates payer enrollments.
 * Uses payer search to find and enroll with payers that require enrollment.
 */

import { useCallback } from 'react';
import { useEnrollmentOperations, useEnrollments } from '@features/enrollments';
import { CheckCircle as CheckCircleIcon, Info as InfoIcon } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import type { PayerResponse } from '@store/generated/payers';
import { useSnackbar } from 'notistack';

import { useEnrollAllRequiredTransactions } from '../../hooks/useEnrollAllRequiredTransactions';
import {
  getEnrollableTransactionTypes,
  getTransactionTypeLabel,
} from '../../utils/enrollmentStatus';
import { extractApiErrorMessage, handleEnrollmentError } from '../../utils/errorUtils';
import { EnrollmentCard } from '../EnrollmentCard';
import { PayerSearchDropdown } from '../PayerSearchDropdown';

interface EnrollPayersStepProps {
  billingEntityId: string | null;
  onBack: () => void;
  onComplete: () => void;
}

export function EnrollPayersStep({ billingEntityId, onBack, onComplete }: EnrollPayersStepProps) {
  const { enqueueSnackbar } = useSnackbar();

  // Get enrollments list to show enrollments initiated during this session
  const { enrollments, isLoading: enrollmentsLoading, refetch: refetchEnrollments } = useEnrollments();
  const { initiate, initiateLoading } = useEnrollmentOperations();
  const { enrollAllRequiredTransactions, enrollingPayerId } = useEnrollAllRequiredTransactions();

  // Handle enrollment initiation from search
  const handleEnroll = useCallback(
    async (payer: PayerResponse) => {
      if (!billingEntityId) {
        enqueueSnackbar('No billing identity found', { variant: 'error' });
        return;
      }
      const payerId = payer.id;
      if (!payerId) return;

      if (getEnrollableTransactionTypes(payer).length === 0) {
        enqueueSnackbar(`${payer.display_name} doesn't require enrollment.`, { variant: 'info' });
        return;
      }

      const { succeeded, failed, errors } = await enrollAllRequiredTransactions(
        payer,
        transactionType =>
          initiate({
            initiateEnrollmentRequest: {
              billing_entity_id: billingEntityId,
              payer_uuid: payerId,
              transaction_type: transactionType,
            },
          }).unwrap(),
      );

      if (failed.length === 0) {
        enqueueSnackbar(
          `Enrollment request submitted for ${payer.display_name} (${succeeded
            .map(getTransactionTypeLabel)
            .join(', ')}). Review usually takes 1-2 weeks.`,
          { variant: 'success' },
        );
        refetchEnrollments();
      } else if (succeeded.length > 0) {
        enqueueSnackbar(
          `Enrolled ${payer.display_name} for ${succeeded
            .map(getTransactionTypeLabel)
            .join(', ')}, but failed for ${failed
            .map(getTransactionTypeLabel)
            .join(', ')}: ${extractApiErrorMessage(errors[errors.length - 1])}`,
          { variant: 'warning', autoHideDuration: 8000 },
        );
        refetchEnrollments();
      } else {
        handleEnrollmentError(errors[errors.length - 1], enqueueSnackbar);
      }
    },
    [billingEntityId, initiate, enqueueSnackbar, refetchEnrollments, enrollAllRequiredTransactions],
  );


  // Handle finish - just close the wizard
  const handleFinish = () => {
    onComplete();
  };

  // Error state - no billing entity
  if (!billingEntityId) {
    return (
      <Box>
        <Alert severity="error">
          No billing identity found. Please complete the previous steps first.
        </Alert>
        <Button variant="outlined" onClick={onBack} fullWidth sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Payer Enrollments (Optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="p">
        Some insurance companies require enrollment before you can submit claims. You can enroll now
        or skip this step and enroll later from the billing settings.
      </Typography>

      {/* Setup Complete Message */}
      <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
        <Typography variant="subtitle2" gutterBottom>
          Setup Complete!
        </Typography>
        <Typography variant="body2">
          You're ready to submit claims! Payer enrollments are optional and can be completed later
          from the billing settings page.
        </Typography>
      </Alert>

      {/* Payer Search */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Search for a Payer to Enroll
        </Typography>
        <PayerSearchDropdown
          onEnroll={handleEnroll}
          isEnrolling={initiateLoading}
          enrollingPayerId={enrollingPayerId}
          placeholder="Search by payer name..."
        />
      </Box>

      {/* Enrollments Started During This Session */}
      {enrollmentsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : enrollments.length > 0 ? (
        <Box sx={{ mb: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Enrollments Started ({enrollments.length})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {enrollments.map(enrollment => (
              <EnrollmentCard
                key={enrollment.stedi_transaction_enrollment_id}
                enrollment={enrollment}
                compact
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            No enrollments required yet. Most payers accept claims without enrollment. Search above
            to check if a specific payer requires it.
          </Typography>
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={onBack} fullWidth disabled={initiateLoading}>
          Back
        </Button>
        <Button variant="contained" onClick={handleFinish} fullWidth disabled={initiateLoading}>
          Finish
        </Button>
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="caption" component="div">
          <strong>Note:</strong> Enrollment processing usually takes 1-2 weeks. You'll be notified
          when enrollments are approved and you can start submitting claims to those insurance
          companies.
        </Typography>
      </Alert>
    </Box>
  );
}
